import type {
    OutboundMessage,
    PunchErrorPayload,
    PunchSuccessPayload,
    SessionTokenPayload
} from './protocol';

interface WKMessageHandler {
    postMessage(data: unknown): void;
}

declare global {
    interface Window {
        webkit?: {
            messageHandlers?: Record<string, WKMessageHandler | undefined>;
        };
        ReactNativeWebView?: {
            postMessage(data: string): void;
        };
        /**
         * Native Android JavascriptInterface bridge.
         * The Android host must register the interface under this exact name:
         *   webView.addJavascriptInterface(bridge, "sevenPunches")
         * Only accepts serialized strings.
         */
        sevenPunches?: {
            postMessage(data: string): void;
        };
    }
}

/**
 * Default WKWebView message handler name used when calling
 * `webkit.messageHandlers.<name>.postMessage()`.
 *
 * The partner native app must register a handler under this name via:
 *   WKWebViewConfiguration.userContentController.add(handler, name: "sevenPunches")
 *
 * Override at runtime with `hostBridge.setWkHandlerName(name)` once the partner
 * confirms their handler name.
 */
const DEFAULT_WK_HANDLER_NAME = 'sevenPunches';

/**
 * Outbound message types that may be broadcast on the iframe path before the
 * target origin has been pinned (i.e. while `targetOrigin` is still `'*'`).
 *
 * Only the `ready` handshake qualifies: the parent's origin is unknown until it
 * replies with `7punches:init` (which pins the origin), and `ready` carries no
 * payload. Every other outbound message — notably `session_token`, which
 * carries a refresh token — must never be posted to `'*'`, or a malicious
 * parent frame could receive it. Such messages are dropped on the iframe path
 * until an `init` pins a specific origin.
 */
const UNPINNED_BROADCAST_ALLOWED = new Set<string>(['7punches:ready']);

function hasWkBridge(): boolean {
    return (
        typeof window !== 'undefined' &&
        window.webkit?.messageHandlers !== undefined
    );
}

function hasRnBridge(): boolean {
    return (
        typeof window !== 'undefined' && window.ReactNativeWebView !== undefined
    );
}

function hasNativeAndroidBridge(): boolean {
    return typeof window !== 'undefined' && window.sevenPunches !== undefined;
}

function hasParentFrame(): boolean {
    return (
        typeof window !== 'undefined' &&
        window.parent !== undefined &&
        window.parent !== window
    );
}

/**
 * Sends typed postMessage events to the native app shell.
 *
 * Routing priority (first available wins):
 *  1. React Native (iOS or Android) — `window.ReactNativeWebView.postMessage(json)`
 *  2. Native iOS WKWebView          — `window.webkit.messageHandlers.sevenPunches.postMessage(data)`
 *  3. Native Android                — `window.sevenPunches.postMessage(json)`
 *  4. iframe / web parent           — `window.parent.postMessage(data, targetOrigin)`
 *
 * No-ops silently when none of the above are available (standalone browser tab).
 */
class HostBridge {
    private targetOrigin = '*';
    private wkHandlerName = DEFAULT_WK_HANDLER_NAME;

    /** Override the postMessage target origin used for the iframe path. */
    setTargetOrigin(origin: string) {
        this.targetOrigin = origin;
    }

    /**
     * Override the WKWebView message handler name.
     * Call this once the partner app confirms the handler they register.
     */
    setWkHandlerName(name: string) {
        this.wkHandlerName = name;
    }

    private send(message: OutboundMessage) {
        try {
            if (hasRnBridge()) {
                // React Native bridge only accepts strings.
                window.ReactNativeWebView!.postMessage(JSON.stringify(message));
                return;
            }

            if (hasWkBridge()) {
                const handler =
                    window.webkit?.messageHandlers?.[this.wkHandlerName];
                if (handler) {
                    handler.postMessage(message);
                    return;
                }
                // Handler name not registered — fall through to next path.
            }

            if (hasNativeAndroidBridge()) {
                // Android JavascriptInterface only accepts strings.
                window.sevenPunches!.postMessage(JSON.stringify(message));
                return;
            }

            if (hasParentFrame()) {
                if (
                    this.targetOrigin === '*' &&
                    !UNPINNED_BROADCAST_ALLOWED.has(message.type)
                ) {
                    // Refuse to broadcast a potentially sensitive message
                    // (e.g. session_token) to an unpinned parent origin.
                    return;
                }
                window.parent.postMessage(message, this.targetOrigin);
            }
        } catch {
            // Outbound messages are best-effort; never crash the terminal.
        }
    }

    /** Terminal is loaded and ready to receive init. */
    ready() {
        this.send({ type: '7punches:ready', payload: undefined });
    }

    /**
     * Hand off a refresh token to the host so it can persist it for the next
     * session resumption attempt.
     */
    sessionToken(payload: SessionTokenPayload) {
        this.send({ type: '7punches:session_token', payload });
    }

    /** Auth session has expired and the terminal cannot recover it. */
    sessionExpired() {
        this.send({ type: '7punches:session_expired', payload: undefined });
    }

    /** Notify the host that a punch completed successfully. */
    punchSuccess(payload: PunchSuccessPayload) {
        this.send({ type: '7punches:punch_success', payload });
    }

    /** Notify the host that a punch failed. */
    punchError(payload: PunchErrorPayload) {
        this.send({ type: '7punches:punch_error', payload });
    }

    /**
     * Notify the host that the terminal has finished its work and the webview
     * should be closed. Used in external-user mode instead of returning to the
     * PIN pad.
     */
    exit() {
        this.send({ type: '7punches:exit', payload: undefined });
    }
}

/** Singleton bridge instance — import and use directly. */
export const hostBridge = new HostBridge();
